import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { canExport } from '@/lib/demoGuard';

async function callPdfEdgeFunction(body: Record<string, any>, token: string) {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const url = `https://${projectId}.supabase.co/functions/v1/gerar-relatorio-pdf`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    let msg = 'Erro ao gerar PDF';
    try { msg = JSON.parse(errorBody).error || msg; } catch {}
    throw new Error(msg);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'relatorio.pdf';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match) filename = match[1];
  }

  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);

  return filename;
}

/**
 * Hook to generate and download PDF reports via the gerar-relatorio-pdf edge function.
 */
export function useGerarRelatorioPdf() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { isDemo } = useAuth();

  const blockedForDemo = useCallback(() => {
    if (!canExport({ isDemo })) {
      toast({
        title: 'Exportação desabilitada',
        description: 'Geração de relatórios não está disponível no modo demonstração.',
        variant: 'destructive',
      });
      return true;
    }
    return false;
  }, [isDemo, toast]);

  const getToken = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) throw new Error('Sessão expirada. Faça login novamente.');
    return token;
  }, []);

  const gerarBoletimPdf = useCallback(async (boletimId: string, contratoId: string) => {
    if (blockedForDemo()) return;
    setLoading(true);
    try {
      const token = await getToken();
      const filename = await callPdfEdgeFunction(
        { tipo: 'boletim_medicao', boletim_id: boletimId, contrato_id: contratoId },
        token,
      );
      toast({ title: 'PDF gerado com sucesso', description: filename });
    } catch (err: any) {
      console.error('PDF generation error:', err);
      toast({ title: 'Erro ao gerar PDF', description: err.message || 'Tente novamente', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, getToken, blockedForDemo]);

  const gerarMensalConsolidadoPdf = useCallback(async (contratoId: string, mesReferencia?: string) => {
    if (blockedForDemo()) return;
    setLoading(true);
    try {
      const token = await getToken();
      const filename = await callPdfEdgeFunction(
        { tipo: 'mensal_consolidado', contrato_id: contratoId, mes_referencia: mesReferencia },
        token,
      );
      toast({ title: 'Relatório Mensal gerado', description: filename });
    } catch (err: any) {
      console.error('PDF generation error:', err);
      toast({ title: 'Erro ao gerar PDF', description: err.message || 'Tente novamente', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, getToken, blockedForDemo]);

  const gerarResumoExecutivoPdf = useCallback(async (contratoId: string) => {
    if (blockedForDemo()) return;
    setLoading(true);
    try {
      const token = await getToken();
      const filename = await callPdfEdgeFunction(
        { tipo: 'resumo_executivo', contrato_id: contratoId },
        token,
      );
      toast({ title: 'Resumo Executivo gerado', description: filename });
    } catch (err: any) {
      console.error('PDF generation error:', err);
      toast({ title: 'Erro ao gerar PDF', description: err.message || 'Tente novamente', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, getToken, blockedForDemo]);

  const gerarCronogramaPdf = useCallback(async (contratoId: string) => {
    if (blockedForDemo()) return;
    setLoading(true);
    try {
      const token = await getToken();
      const filename = await callPdfEdgeFunction(
        { tipo: 'cronograma', contrato_id: contratoId },
        token,
      );
      toast({ title: 'Cronograma PDF gerado', description: filename });
    } catch (err: any) {
      console.error('PDF generation error:', err);
      toast({ title: 'Erro ao gerar PDF', description: err.message || 'Tente novamente', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, getToken, blockedForDemo]);

  const gerarEvmPdf = useCallback(async (contratoId: string) => {
    if (blockedForDemo()) return;
    setLoading(true);
    try {
      const token = await getToken();
      const filename = await callPdfEdgeFunction(
        { tipo: 'evm_indicadores', contrato_id: contratoId },
        token,
      );
      toast({ title: 'Relatório EVM gerado', description: filename });
    } catch (err: any) {
      console.error('PDF generation error:', err);
      toast({ title: 'Erro ao gerar PDF', description: err.message || 'Tente novamente', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, getToken, blockedForDemo]);

  const gerarSaldosPdf = useCallback(async (contratoId: string) => {
    if (blockedForDemo()) return;
    setLoading(true);
    try {
      const token = await getToken();
      const filename = await callPdfEdgeFunction(
        { tipo: 'saldos_contratuais', contrato_id: contratoId },
        token,
      );
      toast({ title: 'Quadro de Saldos gerado', description: filename });
    } catch (err: any) {
      console.error('PDF generation error:', err);
      toast({ title: 'Erro ao gerar PDF', description: err.message || 'Tente novamente', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, getToken, blockedForDemo]);

  return {
    gerarBoletimPdf, gerarMensalConsolidadoPdf, gerarResumoExecutivoPdf,
    gerarCronogramaPdf, gerarEvmPdf, gerarSaldosPdf,
    loading,
  };
}
